import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, MapPin, Phone, Mail, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const endpoint = '/contact.php';

      const response = await axios.post(endpoint, formData, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 10000, // 10 секунд таймаут
      });
      
      if (response.data && response.data.success) {
        setSubmitStatus('success');
        // Очищаем форму после успешной отправки
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
        // Скрываем сообщение через 5 секунд
        setTimeout(() => setSubmitStatus(null), 5000);
      } else {
        throw new Error('Unexpected response format');
      }
    } catch (error) {
      // Логируем ошибку только в development режиме
      if (process.env.NODE_ENV === 'development') {
        console.error('Error submitting form:', error);
      }
      
      // Более детальное сообщение об ошибке для отладки
      let errorMessage = "Une erreur s'est produite. Veuillez réessayer ou nous contacter directement par email.";
      
      if (error.response) {
        // Сервер ответил с ошибкой
        if (error.response.status === 500) {
          errorMessage = "Erreur serveur. Veuillez réessayer plus tard ou nous contacter directement.";
        } else if (error.response.status === 404) {
          errorMessage = "Endpoint non trouvé. Veuillez vérifier la configuration de l'API.";
        } else if (error.response.status === 0 || error.response.status >= 500) {
          errorMessage = "Le serveur est temporairement indisponible. Veuillez réessayer plus tard.";
        }
      } else if (error.request) {
        // Запрос был отправлен, но ответа не получено
        errorMessage = "Impossible de contacter le serveur. Vérifiez votre connexion internet et réessayez.";
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = "La requête a pris trop de temps. Veuillez réessayer.";
      }
      
      setSubmitStatus('error');
      // Скрываем сообщение об ошибке через 8 секунд (больше времени для чтения)
      setTimeout(() => setSubmitStatus(null), 8000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div
        className="relative h-80 bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1423666639041-f56000c27a9a?w=1600)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-white">
            <div className="flex items-center text-sm mb-4">
              <Link to="/" className="opacity-90 hover:opacity-100 transition-opacity">Accueil</Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span>Contact</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold">Contactez-nous</h1>
          </div>
        </div>
      </div>

      {/* Contact Form and Info */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold mb-6">Envoyez-nous un message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Nom complet *
                  </label>
                  <Input
                    id="name"
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full"
                    aria-required="true"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <Input
                    id="email"
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full"
                    aria-required="true"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone
                  </label>
                  <Input
                    id="phone"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                    Sujet *
                  </label>
                  <Input
                    id="subject"
                    type="text"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full"
                    aria-required="true"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full"
                    aria-required="true"
                  />
                </div>

                {submitStatus === 'success' && (
                  <div 
                    role="alert" 
                    aria-live="polite"
                    className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center text-green-800 animate-fadeIn"
                  >
                    <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" aria-hidden="true" />
                    <span>Votre message a été envoyé avec succès. Nous vous répondrons dans les plus brefs délais.</span>
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div 
                    role="alert" 
                    aria-live="assertive"
                    className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center text-red-800 animate-fadeIn"
                  >
                    <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" aria-hidden="true" />
                    <span>Une erreur s'est produite. Veuillez réessayer ou nous contacter directement par email.</span>
                  </div>
                )}

                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full bg-[#c4d600] hover:bg-[#b0c200] text-gray-800 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold mb-6">Nos coordonnées</h2>
              
              <div className="space-y-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-bold text-xl mb-4">Siège social</h3>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <MapPin className="w-5 h-5 text-[#c4d600] mt-1 mr-3" />
                      <div>
                        <p className="text-gray-700">5 Lot. Côte Meunière</p>
                        <p className="text-gray-700">04370 Villars-Colmars, France</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <Mail className="w-5 h-5 text-[#c4d600] mr-3" />
                      <a href="mailto:Contact@artduchalet.com" className="text-gray-700 hover:text-[#c4d600]">
                        Contact@artduchalet.com
                      </a>
                    </div>
                  </div>
                </div>

                <div className="bg-[#c4d600] rounded-lg p-6">
                  <h3 className="font-bold text-xl text-gray-800 mb-3">Horaires d'ouverture</h3>
                  <div className="space-y-2 text-gray-800">
                    <p><span className="font-semibold">Lundi - Vendredi:</span> 9h00 - 12h00, 14h00 - 18h00</p>
                    <p><span className="font-semibold">Samedi:</span> 9h00 - 12h00</p>
                    <p><span className="font-semibold">Dimanche:</span> Fermé</p>
                  </div>
                </div>

                <div>
                  <h3 className="font-bold text-xl mb-4">Suivez-nous</h3>
                  <p className="text-gray-600 mb-4">
                    Restez connectés avec nous sur les réseaux sociaux pour découvrir nos dernières réalisations et actualités.
                  </p>
                  <Button asChild className="bg-[#c4d600] hover:bg-[#b0c200] text-gray-800 font-semibold">
                    <a href="/nos-agences">Voir toutes nos agences</a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;